import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { DeleteOutline as TrashIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

// ─── Props ──────────────────────────────────────────────────

interface BulkDeleteDialogProps {
  open: boolean;
  count: number;
  onClose: () => void;
  onConfirm: () => void;
  translate: (key: string) => string;
}

// ─── Component ──────────────────────────────────────────────

const BulkDeleteDialog: React.FC<BulkDeleteDialogProps> = ({
  open,
  count,
  onClose,
  onConfirm,
  translate,
}) => {
  const [confirmText, setConfirmText] = useState("");

  // Reset on open
  useEffect(() => {
    if (open) {
      setConfirmText("");
    }
  }, [open]);

  // Inject keyframes
  useEffect(() => {
    const id = "bulk-delete-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes bulkDeleteSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bulkDeleteBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const isConfirmed = confirmText.toUpperCase() === "DELETE";
  const canSubmit = isConfirmed;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "bulkDeleteBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "bulkDeleteSlideUp 0.3s ease",
        }}
      >
        {/* Dark terracotta accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #D4572B 0%, #8B3A1E 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "32px 28px 24px", textAlign: "center" }}>
          {/* Trash icon */}
          <Box
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "rgba(212,87,43,0.08)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: "16px",
            }}
          >
            <TrashIcon sx={{ fontSize: "24px", color: "#D4572B" }} />
          </Box>

          {/* Title */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: "#0C1117",
              mb: "12px",
            }}
          >
            {translate("serviceReports.bulk.deleteTitle").replace("{{count}}", String(count))}
          </Box>

          {/* Warning text */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: "#6B7280",
              lineHeight: 1.6,
              mb: "20px",
            }}
          >
            {translate("serviceReports.bulk.deleteWarning")}
          </Box>

          {/* Type DELETE to confirm */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              color: "#0C1117",
              mb: "10px",
            }}
          >
            {translate("serviceReports.actions.typeDeleteToConfirm")}
          </Box>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="- - - - - -"
            autoComplete="off"
            style={{
              width: "100%",
              maxWidth: "320px",
              boxSizing: "border-box",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "15px",
              fontWeight: 600,
              letterSpacing: "0.15em",
              textAlign: "center",
              color: confirmText.length > 0 ? "#DC2626" : "#8C9099",
              backgroundColor: "#F7F5F0",
              border: isConfirmed
                ? "1.5px solid #D4572B"
                : "1.5px solid transparent",
              borderRadius: "10px",
              padding: "12px 16px",
              outline: "none",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#D4572B";
              e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.10)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isConfirmed ? "#D4572B" : "transparent";
              e.target.style.boxShadow = "none";
            }}
          />

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "16px", mt: "24px" }}>
            <Box
              onClick={onClose}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                "&:hover": { color: "#4A4F57" },
              }}
            >
              {translate("serviceReports.actions.cancel")}
            </Box>
            <Box
              onClick={canSubmit ? onConfirm : undefined}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: canSubmit ? "#D4572B" : "#D1CCC4",
                border: canSubmit ? "1.5px solid #D4572B" : "1.5px solid #D1CCC4",
                backgroundColor: "transparent",
                borderRadius: "24px",
                padding: "9px 28px",
                cursor: canSubmit ? "pointer" : "default",
                transition: "all 0.2s ease",
                "&:hover": canSubmit
                  ? { backgroundColor: "rgba(212,87,43,0.06)" }
                  : {},
              }}
            >
              {translate("serviceReports.actions.delete")}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkDeleteDialog;
